import React, { useCallback, useEffect, useState } from 'react';
import { missionControlService } from '../../../services/missionControl.js';
import type { FacilityConfig } from '../../../services/missionControl.js';
import './facility-settings.css';

const PROJECT_LABELS: Record<string, string> = {
  us: 'FIREBASE US',
  uk: 'FIREBASE UK',
  alpha: 'FIREBASE ALPHA',
};

type FacilityTypeValue = 'commercial' | 'legacy' | 'disabled';

interface FacilitySettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function FacilitySettings({
  isOpen,
  onClose,
}: FacilitySettingsProps): React.ReactElement | null {
  const [configs, setConfigs] = useState<FacilityConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Auto-sync from Firestore first, then load configs
      await missionControlService.syncFacilities();
      const result = await missionControlService.getFacilityConfigs();
      if (result.success && result.data) {
        setConfigs(result.data);
      } else {
        setError('Failed to load facility configurations.');
      }
    } catch {
      setError('Failed to load facility configurations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      void fetchConfigs();
    }
  }, [isOpen, fetchConfigs]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  async function handleTypeChange(
    id: string,
    currentType: FacilityTypeValue,
    newType: FacilityTypeValue
  ) {
    if (newType === currentType) return;
    setUpdatingIds((prev) => new Set(prev).add(id));
    // Optimistic update
    setConfigs((prev) => prev.map((c) => (c.id === id ? { ...c, facilityType: newType } : c)));
    try {
      const result = await missionControlService.updateFacilityType(id, newType);
      if (!result.success) {
        setConfigs((prev) =>
          prev.map((c) => (c.id === id ? { ...c, facilityType: currentType } : c))
        );
      }
    } catch {
      setConfigs((prev) =>
        prev.map((c) => (c.id === id ? { ...c, facilityType: currentType } : c))
      );
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  // Group configs by firebaseProject
  const grouped = new Map<string, FacilityConfig[]>();
  for (const cfg of configs) {
    const key = cfg.firebaseProject;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(cfg);
  }

  return (
    <>
      <div
        className={`mc-settings-backdrop${isOpen ? ' mc-settings-backdrop--open' : ''}`}
        onClick={onClose}
        data-testid="settings-backdrop"
      />
      <div
        className={`mc-settings-drawer${isOpen ? ' mc-settings-drawer--open' : ''}`}
        role="dialog"
        aria-label="Facility Settings"
      >
        <div className="mc-settings__glow" />
        <div className="mc-settings__header">
          <div className="mc-settings__title-group">
            <div className="mc-settings__title-bar" />
            <h3 className="mc-settings__title">Facility Settings</h3>
          </div>
          <button className="mc-settings__close-btn" onClick={onClose} aria-label="Close settings">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1l12 12M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {error && <div className="mc-settings__error">{error}</div>}

        <div className="mc-settings__body">
          {loading ? (
            <div className="mc-settings__loading">Loading facilities...</div>
          ) : configs.length === 0 ? (
            <div className="mc-settings__empty">No facilities found in Firestore.</div>
          ) : (
            [...grouped.entries()].map(([project, facilities]) => (
              <div key={project} className="mc-settings__group">
                <div className="mc-settings__group-label">
                  {PROJECT_LABELS[project] ?? project.toUpperCase()}
                </div>
                {facilities.map((cfg) => (
                  <div key={cfg.id} className="mc-settings__facility-row">
                    <div className="mc-settings__facility-info">
                      <span className="mc-settings__facility-name">{cfg.facilityName}</span>
                      <span className="mc-settings__facility-region">{cfg.region}</span>
                    </div>
                    <div
                      className="mc-type-selector"
                      role="radiogroup"
                      aria-label={`Type for ${cfg.facilityName}`}
                    >
                      {(['commercial', 'legacy', 'disabled'] as FacilityTypeValue[]).map((type) => (
                        <button
                          key={type}
                          className={`mc-type-selector__btn mc-type-selector__btn--${type}${cfg.facilityType === type ? ' mc-type-selector__btn--active' : ''}`}
                          role="radio"
                          aria-checked={cfg.facilityType === type}
                          disabled={updatingIds.has(cfg.id)}
                          onClick={() => void handleTypeChange(cfg.id, cfg.facilityType, type)}
                        >
                          {type === 'disabled' ? 'OFF' : type.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
